import React, { useCallback, useRef, useState } from 'react';
import { TenantDetails, useAuthStore } from '../../store/authStore';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Cctv, EyeClosedIcon, EyeIcon, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useMutation } from '@tanstack/react-query';
import axiosClient, { axiosClientAdmin } from '../../lib/auth';
import TenantSelector from './TenantSelector';
import { useFeatureFlagEnabled } from 'posthog-js/react';

interface LoginRequestForm {
  emailAddress: string;
  firstName: string;
  lastName: string;
}

function LoginViaLinkModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [form, setForm] = useState<LoginRequestForm>({
    emailAddress: '',
    firstName: '',
    lastName: '',
  });
  const [formError, setFormError] = useState<string>();

  const loginRequestMutation = useMutation({
    mutationFn: async (data: LoginRequestForm) => {
      const response = await axiosClient.post('/Auth/monitoring/loginrequest', {
        emailAddress: data.emailAddress,
        userName: `${data.firstName} ${data.lastName}`.trim(),
      });
      return response.data;
    },
    onSuccess: () => {
      onClose();
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.message || 'Failed to request login link');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(undefined);
    loginRequestMutation.mutate(form);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Login Link</DialogTitle>
        </DialogHeader>
        <DialogFooter>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.emailAddress}
                  onChange={(e) => setForm({ ...form, emailAddress: e.target.value })}
                  required
                  placeholder="Enter your email"
                  className="bg-inherit"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  required
                  placeholder="Enter your first name"
                  className="bg-inherit"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  required
                  placeholder="Enter your last name"
                  className="bg-inherit"
                />
              </div>
              {formError && (
                <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3">
                  <p className="text-sm text-red-500">{formError}</p>
                </div>
              )}
            </div>
            <Button
              type="submit"
              className="w-full bg-lime text-black"
              disabled={loginRequestMutation.isPending}
            >
              {loginRequestMutation.isPending ? (
                <>
                  Requesting...
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                </>
              ) : (
                'Request Login Link'
              )}
            </Button>
          </form>
          <p>E.g. [shared+YourName@yourdomain.com]</p>
          <p>
            Please enter your work email to receive a login link. If you use a shared inbox, please
            use plus address to get your link.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Login = () => {
  const oneLinkFlag = useFeatureFlagEnabled('one-link');
  const isSsoEntraEnabled = useFeatureFlagEnabled('sso-entra');

  const { login, loginAdmin, determineTenant, logout } = useAuthStore();

  const [password, setPassword] = useState('');
  const [email, setEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string>('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [passwordError, setPasswordError] = useState<string>('');
  const [isLoginViaLinkOpen, setIsLoginViaLinkOpen] = useState(false);

  const [tenantOptions, setTenantOptions] = useState<TenantDetails[]>([]);
  const [tenantSelectorPromise, setTenantSelectorPromise] = useState<
    null | ((resolve: TenantDetails | null) => void)
  >(null);
  const [tenantSelectorOpen, setTenantSelectorOpen] = useState(false);

  const tenantSelectorBlock = useRef(false);

  const navigate = useNavigate();

  const openTenantSelector = (tenants: TenantDetails[]) => {
    tenantSelectorBlock.current = true;
    setTenantOptions(tenants);
    setTenantSelectorOpen(true);

    return new Promise<TenantDetails | null>((resolve) => {
      // check if the promise is already resolved
      setTenantSelectorPromise(() => {
        return resolve;
      });
    });
  };

  const closeTenantSelector = (tenant: TenantDetails | null) => {
    setTenantSelectorOpen(false);

    if (tenant) {
      tenantSelectorBlock.current = false;
    }

    if (tenantSelectorPromise) {
      tenantSelectorPromise(tenant);
    }
    setTenantSelectorPromise(null);
  };

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setLoginError('');
    setEmailError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setLoginError('');
    setPasswordError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoginError('');
    setEmailError('');
    setPasswordError('');

    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!password) {
      setPasswordError('Password is required');
      return;
    }
    if (password.length < 8) {
      setPasswordError('Password needs to be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      // Clear any previous session first to ensure clean state.
      // This prevents login()/loginAdmin() from calling logout() internally,
      // which would clear the TenantId set by determineTenant().
      await logout(false);

      // Determine the tenants that a user has access to (uses basic auth, sets TenantId).
      // This must happen BEFORE login/loginAdmin so the X-Tenant-Id header is set.
      const tenantsError = await determineTenant({ email, password }, openTenantSelector);

      if (tenantSelectorBlock.current) {
        console.log('No tenant selected, blocking login');
        setLoginError('No tenant selected');
        setIsLoading(false);
        tenantSelectorBlock.current = false;
        return;
      }

      // Monitoring Portal
      const loginError = await login({ userName: email, password });

      // Admin Portal (now has X-Tenant-Id header set from determineTenant)
      const adminError = await loginAdmin({ userName: email, password });

      // Fail if:
      // - tenantsError: can't determine tenants (user can't proceed without a tenant)
      // - OR both logins failed (user has no valid credentials)
      if (tenantsError || (loginError && adminError)) {
        setLoginError('Failed to login');
        setIsLoading(false);
        return;
      }

      navigate('/dashboard');
    } catch {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgotpassword');
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      handleLogin(event as unknown as React.FormEvent<HTMLFormElement>);
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  return (
    <>
      <div className="flex items-center justify-between gap-4 border-b border-[#2E3835] bg-[#111] text-white">
        <img src="/logo.svg" className="w-32 border-r p-4" />
        <a className="border-l p-4 px-6 text-sm text-lime" href="https://watchful.co.nz">
          Back to Watchful Website
        </a>
      </div>
      <section className='mx-auto mt-[15vh] max-w-6xl  grid grid-cols-2  max-md:grid-cols-1 gap-6'>
        
      <div className="w-full  rounded-md border border-[#2E3835] bg-card2 shadow-md">
        <div className="border-b border-[#2E3835] p-4">
          <h1 className="mb-1 text-xl font-bold text-white">Login to Watchful</h1>
        </div>
        <div className="flex flex-col gap-4 p-4">
          <form onSubmit={handleLogin}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                autoFocus
                id="email"
                type="email"
                className="mt-1 w-full border-[#2E3835] bg-inherit p-2 text-white shadow-md focus:border-green-500 "
                placeholder="Email"
                value={email}
                onKeyUp={handleKeyUp}
                onChange={handleEmailChange}
                pattern="[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}"
                required
                disabled={isLoading}
              />
              {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
            </div>
            <div className="mt-4">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={
                    'mt-1 w-full border-[#2E3835] bg-inherit p-2 text-white shadow-md focus:border-green-500 '
                  }
                  placeholder="Password"
                  value={password}
                  onKeyUp={handleKeyUp}
                  onChange={handlePasswordChange}
                  required
                  disabled={isLoading}
                />
                <button
                  className="absolute right-0 top-0 p-3 text-lime opacity-70 hover:opacity-100"
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? (
                    <EyeClosedIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              {passwordError && <p className="mt-1 text-sm text-red-500">{passwordError}</p>}
            </div>
            {loginError && (
              <div className="my-4 rounded-md border border-red-500/20 bg-red-500/10 p-3">
                <p className="text-sm text-red-500">{loginError}</p>
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2">
              <Button type="submit" className="w-full bg-lime text-black" disabled={isLoading}>
                {isLoading ? (
                  <>
                    Logging in...
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </div>

            {isSsoEntraEnabled && (
              <div className="mt-4 flex-col gap-y-4 border-t pt-4">
                <Link to={import.meta.env.VITE_ADMIN_API + '/Oidc/entra/login'}>
                  <Button variant="blurred" className="w-full border" type="button">
                    <img src="/company/microsoft-logo.svg" className="h-4 w-4" />
                    Sign in with Microsoft Entra
                  </Button>
                </Link>
              </div>
            )}
          </form>
               {oneLinkFlag && (
          <Button
            variant="outline"
            className="w-full bg-inherit text-lime"
            onClick={() => setIsLoginViaLinkOpen(true)}
          >
            Login via Link
          </Button>
        )}
          <Button
            variant="outline"
            className="w-full bg-inherit text-lime"
            onClick={handleForgotPassword}
          >
            Forgot Password?
          </Button>
        </div>
      </div>

      <div className="w-full max-w-[28rem] rounded-md h-fit">
        <div className="flex flex-col gap-4 p-4">
          <h1 className="text-xl font-bold text-white">Refer + Earn</h1>
          <p className="text-white/70">Refer someone to Watchful. If they successfully connect a camera, you both earn $1,000 NZD.</p>
          <Button
            variant="outline"
            className="w-fit bg-inherit text-lime hover:bg-lime/10 min-w-40"
            onClick={() => window.open('mailto:charlie.anderson@watchful.co.nz?subject=Watchful Referral Program&body=Hi Charlie,%0D%0A%0D%0AI would like to refer someone to Watchful.%0D%0A%0D%0AReferee Details:%0D%0AName: %0D%0ACompany: %0D%0AEmail: %0D%0A%0D%0AThanks!', '_self')}
          >
            Refer now
          </Button>
       <a href="/promo-terms" target="_blank" className="text-lime underline text-xs">Terms & Conditions</a>
        </div>
      </div>
      </section>
      
      <TenantSelector
        open={tenantSelectorOpen}
        tenantOptions={tenantOptions}
        onConfirm={closeTenantSelector}
      />
      <LoginViaLinkModal isOpen={isLoginViaLinkOpen} onClose={() => setIsLoginViaLinkOpen(false)} />
    </>
  );
};

export default Login;
